import React, { useState, useMemo } from 'react';

import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Skeleton,
    Autocomplete,
    TextField,
    Typography,
    Chip,
    Collapse

} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { useGetFlags } from '../../api/flags';

// Flag Edit Dialog Component
const FlagEditDialog = ({ open, onClose, currentFlag, currentRationale, onSave, onDelete }) => {

    console.log("Current Rationale:", currentRationale);

    const { data: flagList, error: flagError, isLoading: isFlagLoading } = useGetFlags(true);

    // State to track if fields have changed from original values
    // const [hasFieldsChanged, setHasFieldsChanged] = React.useState(false);
    const [rationaleCleared, setRationaleCleared] = React.useState(false);
    const [formInitialized, setFormInitialized] = React.useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);

    const { control, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
        defaultValues: {
            flag: currentFlag || '',
            rationale: currentRationale || '',
        }
    });

    // Watch form values for dependent dropdowns
    const watchedFlag = watch('flag');

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            console.log("Dialog opened, resetting form with:", {
                flag: currentFlag,
                rationale: currentRationale
            });

            reset({
                flag: currentFlag || '',
                rationale: currentRationale || '',
            });

            // setHasFieldsChanged(false);
            setRationaleCleared(false);
            setFormInitialized(false);
            setShowDeleteConfirm(false);

            // Use a small delay to ensure form is fully reset before marking as initialized
            setTimeout(() => {
                setFormInitialized(true);
            }, 100); // Increased delay slightly for better reliability
        }
    }, [open, currentFlag, currentRationale, reset]);

    // Check if any field has changed from original values and clear rationale if needed
    React.useEffect(() => {
        // Don't run this logic until the form has been properly initialized
        if (!formInitialized) {
            return;
        }

        const flagChanged = watchedFlag !== (currentFlag || '');

        // If flag changed and we haven't cleared rationale yet
        if (flagChanged && !rationaleCleared) {
            console.log(`Flag changed from "${currentFlag}" to "${watchedFlag}"`);
            console.log("Clearing rationale");
            setValue('rationale', '');
            setRationaleCleared(true);
        }

        // If flag is back to original and rationale was cleared, restore original rationale
        if (!flagChanged && rationaleCleared) {
            console.log("Flag back to original, restoring rationale");
            setValue('rationale', currentRationale || '');
            setRationaleCleared(false);
        }

        // setHasFieldsChanged(flagChanged);
    }, [watchedFlag, currentFlag, currentRationale, rationaleCleared, setValue, formInitialized]);

    const onSubmit = (data) => {
        console.log("Form submitted with data:", data);
        const { flag, rationale } = data;
        const confidenceLevel = "Assigned by User"
        const predictionModel = "User Edit"

        if (flag && flag.trim()) {
            onSave({
                flag: flag.trim(),
                rationale: rationale?.trim() || '',
                confidenceLevel: confidenceLevel,
                predictionModel: predictionModel
            });
        }
        onClose();
    };

    const handleClose = () => {
        reset();
        setShowDeleteConfirm(false);
        onClose();
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = () => {
        if (onDelete) {
            onDelete();
        }
        setShowDeleteConfirm(false);
        onClose();
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    if (isFlagLoading) {
        return (
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Flag</DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <Box sx={{ mb: 2 }}>
                        <Skeleton variant="text" width="120px" height="20px" sx={{ mb: 1 }} />
                        <Skeleton variant="rectangular" width="100%" height="56px" />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Skeleton variant="rectangular" width="80px" height="36px" sx={{ mr: 1 }} />
                    <Skeleton variant="rectangular" width="70px" height="36px" />
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Flag</DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent sx={{ pt: 2 }}>

                        <Controller
                            name="flag"
                            control={control}
                            rules={{ required: 'Flag is required' }}
                            render={({ field, fieldState }) => (
                                <Autocomplete
                                    freeSolo
                                    options={flagList ? flagList.map(flag => flag.name) : []}
                                    value={field.value || ''}
                                    onChange={(event, newValue) => {
                                        // Handle both selection and clearing
                                        field.onChange(newValue || '');
                                    }}
                                    onInputChange={(event, newInputValue, reason) => {
                                        // Handle typing in the input field
                                        if (reason === 'input') {
                                            field.onChange(newInputValue || '');
                                        }
                                    }}
                                    isOptionEqualToValue={(option, value) => option === value}
                                    size="small"
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Flag Name"
                                            placeholder="Select or enter flag name"
                                            variant="outlined"
                                            fullWidth
                                            size="small"
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            sx={{ mb: 2 }}
                                        />
                                    )}
                                />
                            )}
                        />

                        <Controller
                            name="rationale"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label="Rationale"
                                    placeholder="Enter any additional rationale..."
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={4}
                                    size="small"
                                    sx={{ mt: 1 }}
                                />
                            )}
                        />

                    </DialogContent>
                    <DialogActions sx={{ justifyContent: 'space-between' }}>
                        <Box>
                            {currentFlag && (
                                <Button
                                    onClick={handleDeleteClick}
                                    color="error"
                                    variant="outlined"
                                    size="small"
                                >
                                    Delete Flag
                                </Button>
                            )}
                        </Box>
                        <Box>
                            <Button onClick={handleClose} sx={{ mr: 1 }}>Cancel</Button>
                            <Button type="submit" variant="contained" disabled={!watchedFlag}>
                                Save
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={showDeleteConfirm}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this flag? This action cannot be undone.
                    </Typography>
                    {currentFlag && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Flag: <strong>{currentFlag}</strong>
                            </Typography>
                            {currentRationale && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Rationale: {currentRationale}
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>Cancel</Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FlagEditDialog;